import { createEl, clearChildren } from '../dom.js';

/**
 * Pagination по номерам страниц — Prev, до 7 видимых кнопок страниц с
 * многоточием, когда страниц много, затем Next. Клик вызывает onChange(page).
 *
 * Алгоритм для видимых страниц при `maxVisible=7`:
 *   - <=7 страниц: показываем все 1..N
 *   - первые 3 или последние 3: показываем края с многоточием на дальней стороне
 *   - середина: показываем 1, …, current-1, current, current+1, …, N
 */
export class Pagination {
    /**
     * @param {Element} container
     * @param {{onChange: (page: number) => void}} handlers
     */
    constructor(container, { onChange }) {
        this.handlers = { onChange };
        this.el = createEl('nav', {
            className: 'doka-pagination',
            role: 'navigation',
            'aria-label': 'Страницы результатов',
        });
        container.appendChild(this.el);
    }

    /**
     * @param {{page: number, totalPages: number}} state
     */
    update({ page, totalPages }) {
        clearChildren(this.el);
        if (totalPages <= 1) {
            this.el.hidden = true;
            return;
        }
        this.el.hidden = false;

        this.el.appendChild(this.renderButton('‹', page > 1, () => this.handlers.onChange(page - 1), 'doka-pagination-prev'));

        for (const item of computeVisible(page, totalPages)) {
            if (item === 'ellipsis') {
                this.el.appendChild(createEl('span', { className: 'doka-pagination-ellipsis' }, ['…']));
            } else {
                const btn = this.renderButton(String(item), item !== page, () => this.handlers.onChange(item));
                if (item === page) {
                    btn.classList.add('is-current');
                    btn.setAttribute('aria-current', 'page');
                }
                this.el.appendChild(btn);
            }
        }

        this.el.appendChild(this.renderButton('›', page < totalPages, () => this.handlers.onChange(page + 1), 'doka-pagination-next'));
    }

    renderButton(label, enabled, onClick, extraClass = '') {
        const btn = createEl('button', {
            className: 'doka-pagination-btn' + (extraClass ? ' ' + extraClass : ''),
            type: 'button',
            onClick,
        }, [label]);
        if (!enabled) btn.disabled = true;
        return btn;
    }
}

/**
 * @param {number} current
 * @param {number} total
 * @returns {Array<number|'ellipsis'>}
 */
export function computeVisible(current, total) {
    if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i + 1);
    }
    if (current <= 4) {
        return [1, 2, 3, 4, 5, 'ellipsis', total];
    }
    if (current >= total - 3) {
        return [1, 'ellipsis', total - 4, total - 3, total - 2, total - 1, total];
    }
    return [1, 'ellipsis', current - 1, current, current + 1, 'ellipsis', total];
}
